import * as React from 'react';
import {generatePath} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {PageControlBar} from 'shells/control-bar/page-control-bar';
import {StatusBarSecondaryTimestamp} from 'shells/control-bar/status-bar-secondary-timestamp';
// @ts-expect-error - imported from .js/.jsx file
import {ActivityIcon} from 'shells/icon/svg/activity-icon';
// @ts-expect-error - imported from .js/.jsx file
import {CogIcon} from 'shells/icon/svg/cog-icon';
// @ts-expect-error - imported from .js/.jsx file
import {UnloggedIcon} from 'shells/icon/svg/unlogged-icon';
import {TabbedContent} from 'shells/layout/tabbed-content';

import {AiFeatureType} from 'nutshell-graphql-types';
import type {ExternalServiceType} from 'nutshell-graphql-types';
import type {GetSessionUser_loggedInUser as User} from 'nutshell-graphql-types';

import {useGetMeetingsSidebarCounts} from '../../sales/graphql/hooks';
import {useIsAiFeatureEnabled} from '../../setup/ai-features/graphql/hooks/use-is-ai-feature-enabled';

import {AutologInfoBanner} from '../autolog-info-banner';
import {
    getControlBarSubheader,
    getControlBarSubheaderForPhones,
    getServiceNounPlural,
    getServiceNounSingular,
    isUserAutoLogEnabled,
} from '../helpers';
import {LoggedMeetingsTable} from '../logged-meetings-table';
import {UnloggedMeetingsTable} from '../unlogged-meetings-table';

import {TAB_UNLOGGED, TAB_LOGGED, TAB_SETTINGS} from './consts';
import {
    useGetExternalMeetings,
    useAssociateMeetingWithActivity,
    useRemoveExternalMeeting,
    MEETINGS_LIMIT,
} from './graphql/hooks';
import {getIsNotetakerEnabledForServiceType} from './helpers';
import {SettingsTab} from './settings-tab';
import type {ExternalServiceTypeFrontend} from './types';

type VideoMeetingProps = ContextRouter & {
    serviceType: ExternalServiceType;
    userEmail: string;
    user: User;
    isEnabled: boolean;
    enableIntegration: () => void;
    disableIntegration: () => void;
};

type PhoneMeetingProps = ContextRouter & {
    isPerformingInitialSync: boolean;
    firstEnabledIntegrationType: ExternalServiceType | null | undefined;
    serviceType: ExternalServiceTypeFrontend;
};

type Props = PhoneMeetingProps | VideoMeetingProps;

export function MeetingsTableWrapper(props: Props) {
    const {isPerformingInitialSync = false, firstEnabledIntegrationType = null} = props;
    const user = props.serviceType !== 'PHONES' ? props.user : null;
    const hasAutologEnabled =
        user && props.serviceType !== 'PHONES'
            ? isUserAutoLogEnabled(props.serviceType, user)
            : false;

    const {
        externalMeetings: unloggedMeetings,
        isLoading: unloggedLoading,
        fetchNextPage: unloggedFetchNextPage,
        fetchPreviousPage: unloggedFetchPreviousPage,
        count: unloggedCount,
        refetch: refetchUnlogged,
    } = useGetExternalMeetings({
        isLogged: false,
        serviceType: props.serviceType,
        shouldPoll: isPerformingInitialSync,
    });
    const {
        externalMeetings: loggedMeetings,
        isLoading: loggedLoading,
        fetchNextPage: loggedFetchNextPage,
        fetchPreviousPage: loggedFetchPreviousPage,
        count: loggedCount,
        refetch: refetchLogged,
    } = useGetExternalMeetings({
        isLogged: true,
        serviceType: props.serviceType,
        shouldPoll: isPerformingInitialSync,
    });
    const associateMeetingWithActivity = useAssociateMeetingWithActivity();
    // Just want to update the Apollo cache so that the sidebar counts are updated
    const {refetch: refetchMeetingsSidebarCounts} = useGetMeetingsSidebarCounts();

    const removeMeeting = useRemoveExternalMeeting();

    // Check if notetaker is enabled for the service type
    // Will come from the cache first if available
    const {isEnabled: isGMeetNotetakerEnabled} = useIsAiFeatureEnabled(
        AiFeatureType.GOOGLE_MEET_NOTETAKER
    );
    const {isEnabled: isZoomNotetakerEnabled} = useIsAiFeatureEnabled(AiFeatureType.ZOOM_NOTETAKER);
    const {isEnabled: isMicrosoftTeamsNotetakerEnabled} = useIsAiFeatureEnabled(
        AiFeatureType.MICROSOFT_TEAMS_NOTETAKER
    );
    const {isEnabled: isClickToCallNotetakerEnabled} = useIsAiFeatureEnabled(
        AiFeatureType.CLICK_TO_CALL_NOTETAKER
    );
    const {isEnabled: isAircallNotetakerEnabled} = useIsAiFeatureEnabled(
        AiFeatureType.AIRCALL_NOTETAKER
    );
    const {isEnabled: isCallrailNotetakerEnabled} = useIsAiFeatureEnabled(
        AiFeatureType.CALLRAIL_NOTETAKER
    );
    const {isEnabled: isDialpadNotetakerEnabled} = useIsAiFeatureEnabled(
        AiFeatureType.DIALPAD_NOTETAKER
    );
    const {isEnabled: isKixieNotetakerEnabled} = useIsAiFeatureEnabled(
        AiFeatureType.KIXIE_NOTETAKER
    );
    const {isEnabled: isRingCentralNotetakerEnabled} = useIsAiFeatureEnabled(
        AiFeatureType.RINGCENTRAL_NOTETAKER
    );

    const isNotetakerEnabledForServiceType = getIsNotetakerEnabledForServiceType(
        props.serviceType,
        {
            isGMeetNotetakerEnabled,
            isZoomNotetakerEnabled,
            isMicrosoftTeamsNotetakerEnabled,
            isClickToCallNotetakerEnabled,
            isAircallNotetakerEnabled,
            isCallrailNotetakerEnabled,
            isDialpadNotetakerEnabled,
            isKixieNotetakerEnabled,
            isRingCentralNotetakerEnabled,
        }
    );

    const activeTab = props.match.params.tab || TAB_UNLOGGED;
    const meetingCount = activeTab === TAB_UNLOGGED ? unloggedCount || 0 : loggedCount || 0;
    const meetingNoun = activeTab === TAB_UNLOGGED ? 'unlogged' : 'logged';
    const meetings = activeTab === TAB_UNLOGGED ? unloggedMeetings : loggedMeetings;

    const serviceCategoryNounSingular = getServiceNounSingular(props.serviceType);
    const serviceCategoryNounPlural = getServiceNounPlural(props.serviceType);

    // Refetch when tab changes
    React.useEffect(() => {
        if (activeTab === TAB_UNLOGGED) {
            refetchUnlogged();
            refetchMeetingsSidebarCounts();
        } else if (activeTab === TAB_LOGGED) {
            refetchLogged();
            refetchMeetingsSidebarCounts();
        }
    }, [activeTab, refetchUnlogged, refetchLogged, refetchMeetingsSidebarCounts]);

    const tabOptions = [
        {
            id: TAB_UNLOGGED,
            label: `Unlogged ${serviceCategoryNounPlural}`,
            icon: UnloggedIcon,
            href: generatePath(props.match.path, {
                tab: TAB_UNLOGGED,
            }),
            component: (
                <>
                    {hasAutologEnabled && <AutologInfoBanner serviceType={props.serviceType} />}
                    <UnloggedMeetingsTable
                        meetings={unloggedMeetings}
                        isLoading={unloggedLoading}
                        fetchNextPage={unloggedFetchNextPage}
                        fetchPreviousPage={unloggedFetchPreviousPage}
                        onAssociateMeetingWithActivity={associateMeetingWithActivity}
                        onRemoveMeeting={removeMeeting}
                        totalCount={unloggedCount}
                        pageSize={MEETINGS_LIMIT}
                        serviceType={props.serviceType}
                        hasLoggedMeetings={loggedCount > 0}
                        isPerformingInitialSync={isPerformingInitialSync}
                        firstEnabledIntegrationType={firstEnabledIntegrationType}
                        shouldShowSummaryColumn={isNotetakerEnabledForServiceType}
                    />
                </>
            ),
            count: `${unloggedCount}`,
        },
        {
            id: TAB_LOGGED,
            label: `Logged ${serviceCategoryNounPlural}`,
            icon: ActivityIcon,
            href: generatePath(props.match.path, {
                tab: TAB_LOGGED,
            }),
            component: (
                <LoggedMeetingsTable
                    meetings={loggedMeetings}
                    isLoading={loggedLoading}
                    fetchNextPage={loggedFetchNextPage}
                    fetchPreviousPage={loggedFetchPreviousPage}
                    pageSize={MEETINGS_LIMIT}
                    totalCount={loggedCount}
                    serviceType={props.serviceType}
                    isPerformingInitialSync={isPerformingInitialSync}
                    firstEnabledIntegrationType={firstEnabledIntegrationType}
                    hasUnloggedMeetings={unloggedCount > 0}
                    shouldShowSummaryColumn={isNotetakerEnabledForServiceType}
                />
            ),
            count: `${loggedCount}`,
        },
    ];

    if (props.serviceType !== 'PHONES') {
        // @ts-expect-error
        tabOptions.push({
            id: TAB_SETTINGS,
            label: 'Settings',
            icon: CogIcon,
            href: generatePath(props.match.path, {
                tab: TAB_SETTINGS,
            }),
            component: (
                <SettingsTab
                    isEnabled={props.isEnabled}
                    userEmail={props.userEmail}
                    onConnectClick={props.enableIntegration}
                    onDisconnectClick={props.disableIntegration}
                    serviceType={props.serviceType}
                    user={props.user}
                />
            ),
        });
    }

    return (
        <div className='full-height full-width overflow-hidden flex flex-dir-col'>
            <PageControlBar
                statusInfo={
                    activeTab === TAB_SETTINGS
                        ? undefined
                        : {
                              isLoading: unloggedLoading || loggedLoading,
                              primaryText: `${
                                  meetingCount && meetingCount.toLocaleString()
                              } ${meetingNoun} ${
                                  meetingCount === 1
                                      ? serviceCategoryNounSingular
                                      : serviceCategoryNounPlural
                              }`,
                              secondaryText: (
                                  <StatusBarSecondaryTimestamp
                                      prefix={
                                          activeTab === TAB_UNLOGGED
                                              ? `Last ${serviceCategoryNounSingular} synced`
                                              : `Last ${serviceCategoryNounSingular} logged`
                                      }
                                      emptyText={
                                          props.serviceType === 'PHONES'
                                              ? getControlBarSubheaderForPhones(
                                                    loggedCount > 0,
                                                    unloggedCount > 0,
                                                    activeTab,
                                                    Boolean(firstEnabledIntegrationType)
                                                )
                                              : getControlBarSubheader(
                                                    props.serviceType,
                                                    loggedCount > 0,
                                                    unloggedCount > 0,
                                                    activeTab
                                                )
                                      }
                                      // @ts-expect-error
                                      datetime={meetings.length ? meetings[0].startTime : null}
                                  />
                              ),
                          }
                }
                tabs={{
                    selectedTab: activeTab,
                    options: tabOptions,
                }}
            />
            {/* @ts-expect-error */}
            <TabbedContent selectedTab={activeTab} tabOptions={tabOptions} />
        </div>
    );
}
